
import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import CourseNotificationDialog from "@/components/courses/CourseNotificationDialog";
import CoursePlayer from "@/components/courses/CoursePlayer";
import LoginRequiredDialog from "@/components/courses/LoginRequiredDialog";
import CourseDetailsLoading from "@/components/courses/CourseDetailsLoading";
import CourseNotFound from "@/components/courses/CourseNotFound";
import CourseHeader from "@/components/courses/CourseHeader";
import CourseLessonsList from "@/components/courses/CourseLessonsList";
import CourseSidebar from "@/components/courses/CourseSidebar";
import PaymentStatusDialog from "@/components/payment/PaymentStatusDialog";

type TextDirection = "ltr" | "rtl";

interface CourseData {
  id: string;
  title: string;
  description: string;
  level: string;
  price: string;
  duration: string;
  status: string;
  image_url: string | null;
  what_you_learn: string[] | null;
  text_direction: TextDirection;
  user_id: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

interface LessonData {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  image_url: string | null;
  lesson_order: number;
}

const CourseDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [course, setCourse] = useState<CourseData | null>(null);
  const [lessons, setLessons] = useState<LessonData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [courseExists, setCourseExists] = useState(true);
  const [paymentStatusDialog, setPaymentStatusDialog] = useState<{
    isOpen: boolean;
    status: "success" | "failed" | "pending" | null;
    message?: string;
    error?: any;
  }>({
    isOpen: false,
    status: null
  });
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const urlPaymentStatus = searchParams.get('status');

  const checkEnrollment = useCallback(async () => {
    if (!user || !id) return false;
    
    try {
      console.log("Checking enrollment for user:", user.id, "in course:", id);
      const { data, error } = await supabase
        .from('course_enrollments')
        .select('*')
        .eq('course_id', id)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error checking enrollment:', error);
        return false;
      }
      
      const isUserEnrolled = !!data;
      console.log("User enrollment status:", isUserEnrolled);
      setIsEnrolled(isUserEnrolled);
      return isUserEnrolled;
    } catch (error) {
      console.error('Error checking enrollment:', error);
      return false;
    }
  }, [user, id]);

  useEffect(() => {
    if (urlPaymentStatus === 'success' && user && id) {
      console.log("Payment success detected, checking enrollment");
      checkEnrollment();
    }
  }, [urlPaymentStatus, user, id, checkEnrollment]);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching course with ID:", id);
        
        if (!id) {
          console.error("No course ID provided");
          setCourseExists(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('courses')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        
        if (error) {
          console.error('Error fetching course:', error);
          setCourseExists(false);
          return;
        }
        
        if (!data) {
          console.log("No course data returned for ID:", id);
          setCourseExists(false);
          return;
        }
        
        console.log("Course found:", data.title, "Published:", data.published);
        
        const courseData: CourseData = {
          ...data,
          text_direction: (data.text_direction as string || "ltr") as TextDirection
        };
        
        setCourse(courseData);
        setCourseExists(true);

        const { data: lessonsData, error: lessonsError } = await supabase
          .from('course_lessons')
          .select('*')
          .eq('course_id', id)
          .order('lesson_order', { ascending: true });

        if (lessonsError) {
          console.error('Error fetching lessons:', lessonsError);
        } else {
          console.log(`Found ${lessonsData?.length || 0} lessons`);
          setLessons(lessonsData || []);
        }

      } catch (error) {
        console.error('Error fetching course:', error);
        setCourseExists(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourse();
    
    if (user && id) {
      checkEnrollment();
    }
  }, [id, user, checkEnrollment]);

  useEffect(() => {
    let intervalId: number | undefined;
    
    if (urlPaymentStatus === 'success' && user && id) {
      checkEnrollment();
      
      let checkCount = 0;
      intervalId = window.setInterval(() => {
        checkCount++;
        console.log("Checking enrollment attempt:", checkCount);
        checkEnrollment();
        
        if (checkCount >= 5) {
          clearInterval(intervalId);
        }
      }, 2000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [urlPaymentStatus, user, id, checkEnrollment]);

  const handleEnroll = async () => {
    if (!user) {
      setIsLoginDialogOpen(true);
      return;
    }
    
    if (isEnrolled) {
      navigate(`/courses/${id}/lessons`);
    } else {
      toast({
        title: "Enrollment initiated",
        description: "Complete your enrollment below.",
      });
      
      const playerSection = document.getElementById('course-player');
      if (playerSection) {
        playerSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleGetNotified = () => {
    if (!user) {
      setIsLoginDialogOpen(true);
      return;
    }
    setIsNotificationDialogOpen(true);
  };

  const getCurrentUrl = () => {
    return window.location.pathname;
  };

  useEffect(() => {
    const status = searchParams.get('status');
    const message = searchParams.get('message');
    
    if (status === 'success' || status === 'failed' || status === 'pending') {
      setPaymentStatusDialog({
        isOpen: true,
        status: status as "success" | "failed" | "pending",
        message: message || undefined
      });
      
      // Clear URL parameters
      setSearchParams({});
      
      if (status === 'success' && id) {
        // When payment is successful, redirect to lessons page after a short delay
        setTimeout(() => {
          navigate(`/courses/${id}/lessons`);
        }, 3000);
      }
    }
  }, [searchParams, setSearchParams, navigate, id]);

  const closePaymentDialog = () => {
    setPaymentStatusDialog(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <Layout
      title={course?.title}
      description={course?.description}
      image={course?.image_url || undefined}
      type="product"
      path={`/courses/${id}`}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/courses">
          <Button variant="ghost" className="mb-6 pl-0 hover:bg-transparent">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to courses
          </Button>
        </Link>
        
        {isLoading ? (
          <CourseDetailsLoading />
        ) : !courseExists || !course ? (
          <CourseNotFound />
        ) : (
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2">
                <CourseHeader 
                  title={course.title}
                  level={course.level}
                  duration={course.duration}
                  status={course.status}
                  description={course.description}
                  isEnrolled={isEnrolled}
                  textDirection={course.text_direction}
                />
                
                <CourseLessonsList 
                  lessons={lessons}
                  isEnrolled={isEnrolled}
                  courseStatus={course.status}
                />
              </div>
              
              <div>
                <CourseSidebar 
                  imageUrl={course.image_url}
                  title={course.title}
                  price={course.price}
                  duration={course.duration}
                  isEnrolled={isEnrolled}
                  courseStatus={course.status}
                  onEnroll={handleEnroll}
                  onGetNotified={handleGetNotified}
                />
              </div>
            </div>
            
            {user && (
              <div id="course-player">
                {course.status === "Enrolling" && (
                  <CoursePlayer 
                    courseId={course.id} 
                    courseName={course.title} 
                    coursePrice={course.price}
                    lessons={lessons}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {course && (
        <CourseNotificationDialog 
          courseId={course.id} 
          courseName={course.title} 
          isOpen={isNotificationDialogOpen} 
          onClose={() => setIsNotificationDialogOpen(false)} 
        />
      )}
      
      <LoginRequiredDialog
        isOpen={isLoginDialogOpen}
        onClose={() => setIsLoginDialogOpen(false)}
        redirectUrl={getCurrentUrl()}
        message="Please log in to continue with this course."
      />
      
      <PaymentStatusDialog
        isOpen={paymentStatusDialog.isOpen}
        onClose={closePaymentDialog}
        status={paymentStatusDialog.status}
        message={paymentStatusDialog.message}
      />
    </Layout>
  );
};

export default CourseDetails;
